import { wrap } from '@popmotion/popcorn'
import { AnimatePresence } from 'framer-motion'
import { useContext } from 'react'

import { PaginatedViewContext } from './PaginatedView'
import { StSlide, StSliderContainer } from './Slides.styled'

/**
 * Variants define visual states that a motion component can be in at any given time.
 * These can be dynamic - here the enter and exit variants are functions that return
 * different values based on the current direction.
 */
const xOffset = 100
const variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? xOffset : -xOffset,
    opacity: 0,
  }),
  active: {
    x: 0,
    opacity: 1,
    transition: { delay: 0.2 },
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -xOffset : xOffset,
    opacity: 0,
  }),
}

const swipeConfidenceThreshold = 10000
const swipePower = (offset: number, velocity: number) => {
  return Math.abs(offset) * velocity
}

export function Slides() {
  const { currentSlide, setCurrentSlideIndex, direction, slides } = useContext(PaginatedViewContext)

  return (
    <StSliderContainer>
      <AnimatePresence initial={false} custom={direction}>
        <StSlide
          key={currentSlide}
          className='slide'
          data-page={currentSlide}
          variants={variants}
          initial='enter'
          animate='active'
          exit='exit'
          drag='x'
          onDragEnd={(event, { offset, velocity }) => {
            const swipe = swipePower(offset.x, velocity.x)

            if (!slides) {
              return
            }

            let nextSlide = currentSlide + 1
            if (swipe > swipeConfidenceThreshold) {
              nextSlide = currentSlide - 1
            }

            /**
             * To make sure you can't drag past or before the slides,
             * we exit if the next slide would fall out of the range.
             *
             * We could do this with a Clamp, and go ahead with setting
             * the slide index anyway, but then the animation breaks.
             */
            if (nextSlide >= slides.length || nextSlide < 0) {
              return
            }

            setCurrentSlideIndex(wrap(0, slides.length, nextSlide))
          }}
          dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
          custom={direction}
        >
          {slides?.[currentSlide]}
        </StSlide>
      </AnimatePresence>
    </StSliderContainer>
  )
}
